import React, { useContext, useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import { Close } from '@mui/icons-material';
import {
  Box,
  Button,
  Container,
  Dialog,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  Radio,
  RadioGroup,
  Typography
} from '@mui/material';
import {
  gray10,
  gray20,
  gray70,
  gray80,
  gray90,
  gray100,
  primaryButtonBackgroundColor,
  primaryColor,
  tertiaryButtonOutlinedColor
} from '../../../constants/Colors';
import SideBar from '../../uiParts/SideBar';
import { sideBarWidth } from '../../../constants/Location';
import CustomLoading from '../../uiParts/CustomLoading';
import CustomTextfield from '../../uiParts/CustomTextfield';

import { yupResolver } from '@hookform/resolvers/yup';
import { Controller, useForm } from 'react-hook-form';
import {
  co2SettingRowChangeSchema,
  co2SettingUpdateSchema
} from '../../../validation/co2Setting';

import { AuthContext } from '../../../contexts/auth';
import { AxiosError } from 'axios';
import {
  getCo2Setting,
  saveCo2Setting,
  updateCo2Setting
} from '../../../api/db/co2Setting';
import { response } from '../../../api/db';
import { common } from '../../../modules/error';
import { Co2SettingUseFlg } from '../../../constants/Enum';
import CustomHead from '../CustomHead';
import { sentryLog } from '../../../config/sentry';
import {
  Co2SettingGetType,
  Co2SettingValueType
} from '../../../validation/co2Setting/co2SettingType';

const styles = {
  dialogButton: {
    ...primaryButtonBackgroundColor
  },
  dialogRadio: {
    '&.Mui-checked': {
      color: primaryColor
    },
    color: gray90
  },
  dialogRadioLabel: {
    '& .Mui-disabled': {
      color: gray80
    },
    color: gray100
  },
  dialogRadioWrapper: {
    flexDirection: 'row',
    m: '16px 0'
  },
  dialogTitle: {
    color: gray100,
    fontSize: '22px',
    fontWeight: '500'
  },
  wrapper: {
    '@media screen and (max-width:960px)': {
      margin: '24px 16px'
    },
    margin: '32px'
  },
  headerContainer: {
    '@media screen and (max-width:960px)': {
      flexDirection: 'column'
    },
    mb: '24px'
  },
  title: {
    '@media screen and (max-width:600px)': {
      fontSize: '24px',
      pr: '8px',
      mr: '8px'
    },
    color: gray100,
    fontSize: '32px',
    fontWeight: 500,
    pr: '16px',
    mr: '16px'
  },
  settingName: {
    '@media screen and (max-width:600px)': {
      fontSize: '16px'
    },
    fontSize: '24px',
    color: gray100
  },
  calcelButton: {
    ...tertiaryButtonOutlinedColor,
    ml: '16px',
    p: '10px 16px',
    borderRadius: '4px',
    fontSize: '16px',
    hight: '44px',
    '@media screen and (max-width:960px)': {
      mt: '24px',
      ml: '0'
    }
  },
  saveButton: {
    ...primaryButtonBackgroundColor,
    ml: '16px',
    p: '10px 16px',
    borderRadius: '4px',
    fontSize: '16px',
    hight: '44px',
    '@media screen and (max-width:960px)': {
      display: 'none'
    }
  },
  spButtonContainer: {
    width: '100%',
    backgroundColor: 'rgba(255, 255, 255, 0.80)',
    p: '12px 16px',
    position: 'fixed',
    bottom: '0',
    left: '0',
    zIndex: 1,
    '@media screen and (min-width:961px)': {
      display: 'none'
    }
  },
  saveButtonSp: {
    ...primaryButtonBackgroundColor,
    p: '10px 16px',
    borderRadius: '4px',
    fontSize: '16px',
    width: 'calc(100% - 32px)',
    hight: '44px'
  },
  buttonContainer: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    '@media screen and (max-width:960px)': {
      flexDirection: 'column-reverse'
    }
  }
};

const tableStyles = {
  tableContainer: {
    border: '1px solid' + gray70,
    borderBottom: 'none'
  },
  headCell: {
    '@media screen and (max-width:600px)': {
      fontSize: '14px',
      p: '12px 16px'
    },
    fontSize: '16px',
    fontWeight: 500,
    color: gray90,
    p: '10px 32px',
    borderRight: '1px solid' + gray70,
    borderBottom: '1px solid' + gray70
  },
  subHeader: {
    '@media screen and (max-width:600px)': {
      fontSize: '12px',
      p: '7px 16px'
    },
    backgroundColor: gray20,
    fontSize: '14px',
    color: gray100,
    p: '10px 32px',
    borderBottom: '1px solid' + gray70
  },
  cell: {
    '@media screen and (max-width:600px)': {
      fontSize: '14px',
      p: '18px 16px'
    },
    fontSize: '16px',
    color: gray100,
    p: '20px 32px',
    backgroundColor: gray10,
    borderRight: '1px solid' + gray70,
    borderBottom: '1px solid' + gray70
  },
  newMark: {
    '@media screen and (max-width:600px)': {
      width: '8px',
      height: '8px',
      mr: '4px'
    },
    width: '10px',
    height: '10px',
    mr: '7px'
  }
};

type RowData = {
  material_id: number;
  material_name: string;
  material_unit: string;
  material_remark: string | null;
  material_sort: number;
  calculation_specification: string | null;
  new_flg: number;
  value: number;
  remark: string | null;
};

type Data = {
  category_id: number;
  category_name: string;
  rowDatas: RowData[];
};

const CsvConfirmation = (): React.ReactElement => {
  const location = useLocation();
  const { state } = location;
  const csvData = (state as { csvData: string[][] }).csvData;
  //console.log('CSV data:', csvData); // デバッグ用ログ
  const navigate = useNavigate();
  const { id } = useParams();
  const {
    checkLoginStatus,
    setErrorText,
    setInfoText,
    setSnackbarOpen,
    userId,
    userRank,
    userType
  } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [useDialogOpen, setUseDialogOpen] = useState(false);
  const [isDefault, setIsDefault] = useState(true);
  const [isButtonDisabled, setIsButtonDisabled] = useState(false);
  const [saveMethod, setSaveMethod] = useState('新規保存');
  const [useFlg, setUseFlg] = useState(Co2SettingUseFlg.notUse);
  let fetchedData: Co2SettingGetType = {
    id: 0,
    name: '',
    use_flg: 0,
    default_flg: 0,
    co2_setting_values: {
      '0': {
        id: 0,
        category_id: 0,
        category_name: '',
        category_sort: 0,
        material_id: 0,
        material_name: '',
        material_unit: '',
        material_remark: null,
        material_sort: 0,
        calculation_specification: null,
        new_flg: 0,
        value: 0,
        remark: null,
        created_at: '',
        updated_at: ''
      }
    },
    created_at: '',
    updated_at: ''
  };

  // CO2基準値表示データ
  const [displayData, setDisplayData] = useState<Data[]>([]);
  // CO2基準値保存API用データ
  const [apiData, setApiData] = useState<
    { material_id: number; value: number; remark: string | null }[]
  >([]);

  const {
    control,
    formState: { errors },
    handleSubmit,
    reset,
    watch
  } = useForm<Co2SettingUpdateType>({
    resolver: yupResolver(co2SettingUpdateSchema)
  });

  useEffect(() => {
    void (async () => {
      setLoading(true);
      if (!checkLoginStatus) return navigate('/auth/login');
      const result = await checkLoginStatus();
      if (!result.check) {
        navigate('/auth/login');
        return;
      }
      try {
        // CO2基準値の取得
        fetchedData = await getCo2Setting();

        reset({ name: fetchedData.name });

        const co2SettingValues = fetchedData.co2_setting_values;
        const co2SettingValuesArray = Object.values(co2SettingValues);

        if (fetchedData.default_flg === 0) {
          setIsDefault(true);
        }
        if (fetchedData.use_flg === Co2SettingUseFlg.use) {
          setUseFlg(Co2SettingUseFlg.use);
        }

        // 表示用データをセット
        await buildDisplayData(co2SettingValuesArray);

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (error: any) {
        sentryLog(error);
        const axiosError = error as AxiosError;
        if (axiosError.isAxiosError && axiosError.response) {
          const loginErrorResponse = axiosError.response as response;
          setErrorText && setErrorText(loginErrorResponse.data.message);
          setSnackbarOpen && setSnackbarOpen(true);
        } else {
          setErrorText && setErrorText(common);
          setSnackbarOpen && setSnackbarOpen(true);
        }
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const dialogButtonDisabledFlag = (): boolean => {
    return watch('name') === undefined || '' || Boolean(errors.name);
  };

  const onSubmit = async (): Promise<void> => {
    if (saveMethod === '上書き保存' && useFlg === Co2SettingUseFlg.use) {
      await onSave();
    } else {
      setUseFlg(Co2SettingUseFlg.notUse);
      setSaveDialogOpen(false);
      setUseDialogOpen(true);
    }
  };

  const onSave = async (): Promise<void> => {
    setLoading(true);
    try {
      if (id && saveMethod === '上書き保存') {
        fetchedData = await updateCo2Setting(
          id,
          watch('name'),
          useFlg,
          apiData
        );
        window.dataLayer.push({
          event: 'click',
          screen: window.location.pathname,
          category: 'button',
          action: 'standard_update',
          user_id: userId,
          user_type: userType,
          user_rank: userRank
        });
      } else {
        fetchedData = await saveCo2Setting(watch('name'), useFlg, apiData);
        window.dataLayer.push({
          event: 'click',
          screen: window.location.pathname,
          category: 'button',
          action: 'standard_new',
          user_id: userId,
          user_type: userType,
          user_rank: userRank
        });
      }

      const co2SettingValues = fetchedData.co2_setting_values;
      const co2SettingValuesArray = Object.values(co2SettingValues);

      // 表示用データをセット
      await buildDisplayData(co2SettingValuesArray);
      setIsDefault(true);

      setErrorText && setErrorText('');
      setInfoText && setInfoText('CO2基準値を保存しました');
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      sentryLog(error);
      const axiosError = error as AxiosError;
      if (axiosError.isAxiosError && axiosError.response) {
        const loginErrorResponse = axiosError.response as response;
        setErrorText && setErrorText(loginErrorResponse.data.message);
      } else {
        setErrorText && setErrorText(common);
      }
      setInfoText && setInfoText('');
    } finally {
      setSnackbarOpen && setSnackbarOpen(true);
      navigate('/standard/list');
    }
  };

  // 表示用データ配列を生成してセット
  const buildDisplayData = async (
    co2SettingValues: Co2SettingValueType[]
  ): Promise<void> => {
    // 一意なカテゴリーIDを取得
    const uniqueCategoryIds = co2SettingValues
      .map((item) => {
        return { id: item.category_id };
      })
      .filter(
        (value, index, self): boolean =>
          self.findIndex((item) => item.id === value.id) === index
      )
      .sort((a, b) => a.id - b.id);

    // 一意なカテゴリーIDに対応するカテゴリー名を取得
    const uniqueCategories = uniqueCategoryIds.map((uniqueCategoryId) => {
      const matchingItem = co2SettingValues.find(
        (item: { category_id: number }) =>
          item.category_id === uniqueCategoryId.id
      );

      if (!matchingItem) {
        throw new Error();
      }
      return {
        id: uniqueCategoryId.id,
        name: matchingItem.category_name
      };
    });

    // 表示用データを作成
    const displayData = uniqueCategories.map((category) => ({
      category_id: category.id,
      category_name: category.name,
      rowDatas: co2SettingValues
        .filter((item) => item.category_id === category.id)
        .map((item) => {
          const csvItem = csvData.find((csvRow) => {
            //console.log(`Material ID: ${csvRow[1]}, CSV Row:`, csvRow);
            return Number(csvRow[1]) === item.material_id;
          });
          return {
            material_id: item.material_id,
            material_name: item.material_name,
            material_unit: item.material_unit,
            material_remark: item.material_remark,
            material_sort: item.material_sort,
            calculation_specification: item.calculation_specification,
            new_flg: item.new_flg,
            value: csvItem ? Number(csvItem[3]) : item.value, // CSVの値で上書き
            remark: csvItem ? csvItem[6] : item.remark // CSVのremarkで上書き
          };
        })
        .sort((a, b) => a.material_sort - b.material_sort)
    }));
    setDisplayData(displayData);
    setApiData(
      displayData
        .map((item) =>
          item.rowDatas.map((row) => ({
            material_id: row.material_id,
            value: row.value,
            remark: row.remark
          }))
        )
        .flat()
    );
  };

  return (
    <>
      <CustomHead title="CSV取込確認" />
      <CustomLoading loading={loading} />
      <Grid width="100%">
        <SideBar />
        <Box sx={styles.spButtonContainer}>
          <Button
            disabled={isButtonDisabled}
            onClick={() => setSaveDialogOpen(true)}
            sx={styles.saveButtonSp}
          >
            登録する
          </Button>
        </Box>
        <Grid
          sx={{
            '@media screen and (min-width:961px)': {
              ml: sideBarWidth
            },
            minWidth: '900px',
            overflowX: 'scroll',
            mb: '200px'
          }}
        >
          <Box sx={styles.wrapper}>
            <Grid
              container
              flexWrap="nowrap"
              justifyContent="space-between"
              sx={styles.headerContainer}
            >
              <Grid item>
                <Typography flexShrink={0} sx={styles.title}>
                  CSV取込確認
                </Typography>
              </Grid>
              <Grid item flexShrink={0} sx={styles.buttonContainer}>
                <Grid>
                  <Button
                    onClick={() => navigate('/standard')}
                    sx={styles.calcelButton}
                    variant="outlined"
                  >
                    キャンセル
                  </Button>
                </Grid>
                <Button
                  disabled={isButtonDisabled}
                  onClick={() => setSaveDialogOpen(true)}
                  sx={styles.saveButton}
                >
                  登録する
                </Button>
              </Grid>
            </Grid>
            <Grid sx={tableStyles.tableContainer}>
              <Grid container>
                <Grid item xs={2.4} sx={tableStyles.headCell}>
                  <Typography>部材名</Typography>
                </Grid>
                <Grid item xs={2.0} sx={tableStyles.headCell}>
                  <Typography>CO2基準値</Typography>
                </Grid>
                <Grid item xs={1.8} sx={tableStyles.headCell}>
                  <Typography>単位</Typography>
                </Grid>
                <Grid item xs={2.2} sx={tableStyles.headCell}>
                  <Typography>基準CO2算定仕様</Typography>
                </Grid>
                <Grid
                  item
                  xs={3.6}
                  sx={[tableStyles.headCell, { borderRight: 'none' }]}
                >
                  <Typography>備考</Typography>
                </Grid>
              </Grid>
              {displayData.map((item, index) => (
                <Grid container key={index}>
                  <Grid item xs={12} sx={tableStyles.subHeader}>
                    <Typography>{item.category_name}</Typography>
                  </Grid>
                  {item.rowDatas.map((rowData, index) => (
                    <TableRow
                      apiData={apiData}
                      rowData={rowData}
                      key={index}
                      setApiData={setApiData}
                      setIsButtonDisabled={setIsButtonDisabled}
                    />
                  ))}
                </Grid>
              ))}{' '}
            </Grid>
          </Box>
        </Grid>
      </Grid>

      {/* 保存方法、名前設定ダイアログ */}
      <Dialog open={saveDialogOpen} onClose={() => setSaveDialogOpen(false)}>
        <Container
          sx={{
            p: '24px',
            width: '560px',
            '@media screen and (max-width:700px)': {
              m: '0',
              width: '100%'
            }
          }}
        >
          <Grid container alignItems="center" justifyContent="space-between">
            <Typography sx={styles.dialogTitle}>CO2基準値を新規保存</Typography>
            <IconButton onClick={() => setSaveDialogOpen(false)}>
              <Close />
            </IconButton>
          </Grid>

          <FormControl>
            <RadioGroup
              defaultValue={'新規保存'}
              onChange={(e) => setSaveMethod(e.target.value)}
              sx={styles.dialogRadioWrapper}
            >
              <FormControlLabel
                control={<Radio sx={styles.dialogRadio} />}
                label="新規保存"
                sx={[styles.dialogRadioLabel, { m: '0 24px 0 0' }]}
                value="新規保存"
              />
              <FormControlLabel
                control={<Radio sx={styles.dialogRadio} />}
                disabled={isDefault}
                label="上書き保存"
                sx={[styles.dialogRadioLabel, { m: '0 24px 0 0' }]}
                value="上書き保存"
              />
            </RadioGroup>
          </FormControl>

          <Controller
            control={control}
            name="name"
            render={({ field }) => (
              <CustomTextfield
                {...field}
                error={Boolean(errors.name)}
                formMb={'40px'}
                helperText={errors.name?.message}
                labelText={'名前を入力すると識別しやすくなります'}
                value={field.value}
                width={'100%'}
              />
            )}
          />

          <Button
            disabled={dialogButtonDisabledFlag()}
            fullWidth
            onClick={() => {
              void (async () => {
                await handleSubmit(async () => await onSubmit())();
              })();
            }}
            sx={styles.dialogButton}
            variant="contained"
          >
            保存する
          </Button>
        </Container>
      </Dialog>

      {/* 利用設定ダイアログ */}
      <Dialog open={useDialogOpen} onClose={() => setUseDialogOpen(false)}>
        <Container
          sx={{
            p: '24px',
            width: '560px',
            '@media screen and (max-width:700px)': {
              m: '0',
              width: '100%'
            }
          }}
        >
          <Grid container alignItems="center" justifyContent="space-between">
            <Typography sx={styles.dialogTitle}>利用設定</Typography>
            <IconButton onClick={() => setUseDialogOpen(false)}>
              <Close />
            </IconButton>
          </Grid>

          <Typography sx={{ color: gray100, mt: '16px' }}>
            建材検索するときに、このCO2基準値を利用しますか？
          </Typography>

          <FormControl>
            <RadioGroup
              defaultValue={Co2SettingUseFlg.notUse}
              onChange={(e) => setUseFlg(parseInt(e.target.value, 10))}
              sx={styles.dialogRadioWrapper}
            >
              <FormControlLabel
                control={<Radio sx={styles.dialogRadio} />}
                label="利用する"
                sx={[styles.dialogRadioLabel, { m: '0 24px 0 0' }]}
                value={Co2SettingUseFlg.use}
              />
              <FormControlLabel
                control={<Radio sx={styles.dialogRadio} />}
                label="利用しない"
                sx={[styles.dialogRadioLabel, { m: '0 24px 0 0' }]}
                value={Co2SettingUseFlg.notUse}
              />
            </RadioGroup>
          </FormControl>

          <Button
            fullWidth
            onClick={() => {
              void (async () => {
                await handleSubmit(async () => await onSave())();
              })();
            }}
            sx={styles.dialogButton}
            variant="contained"
          >
            保存する
          </Button>
        </Container>
      </Dialog>
    </>
  );
};

export default CsvConfirmation;

type TableRowProps = {
  apiData: { material_id: number; value: number; remark: string | null }[];
  rowData: RowData;
  setApiData: ([]) => void;
  setIsButtonDisabled: (value: boolean) => void;
};

type Co2SettingUpdateType = {
  name: string;
  remark?: string | null;
};

const TableRow = (props: TableRowProps): React.ReactElement => {
  const { apiData, rowData, setApiData, setIsButtonDisabled } = props;

  const {
    control,
    formState: { errors },
    reset,
    watch
  } = useForm<{ value: number; remark?: string | null }>({
    resolver: yupResolver(co2SettingRowChangeSchema),
    mode: 'onChange'
  });

  useEffect(() => {
    reset({ value: rowData.value });
  }, []);

  useEffect(() => {
    if (watch('value') === undefined || '' || Boolean(errors.value)) {
      setIsButtonDisabled(true);
    } else {
      setIsButtonDisabled(false);
    }
  }, [watch('value'), errors.value]);

  const changeValue = async (): Promise<void> => {
    const data = [
      ...apiData.filter((item) => item.material_id !== rowData.material_id),
      {
        material_id: rowData.material_id,
        value: watch('value'),
        remark: watch('remark') || null
      }
    ];
    setApiData(data);
  };

  return (
    <Grid container>
      <Grid
        item
        xs={2.4}
        sx={[
          tableStyles.cell,
          {
            pl: '14px',
            '@media screen and (max-width:600px)': {
              pl: '4px'
            }
          }
        ]}
      >
        <Grid container flexDirection="row" alignItems="center">
          <Box>{rowData.material_name}</Box>
        </Grid>
      </Grid>
      <Grid
        item
        xs={2.0}
        sx={[
          tableStyles.cell,
          {
            p: '12px 32px',
            '@media screen and (max-width:600px)': {
              p: '12px 16px'
            }
          }
        ]}
      >
        <Controller
          control={control}
          name="value"
          render={({ field }) => (
            <CustomTextfield
              {...field}
              width="100%"
              placeholder="○○"
              formMb="0"
              spHeight="32px"
              error={Boolean(errors.value)}
              helperText={errors.value?.message}
              onBlur={() => changeValue()}
            />
          )}
        />
      </Grid>
      <Grid item xs={1.8} sx={tableStyles.cell}>
        <Box>kg-CO2/{rowData.material_unit}</Box>
      </Grid>
      <Grid item xs={2.2} sx={tableStyles.cell}>
        <Box>{rowData.calculation_specification}</Box>
      </Grid>
      <Grid item xs={3.6} sx={[tableStyles.cell, { borderRight: 'none' }]}>
        <Controller
          control={control}
          name="remark"
          defaultValue={rowData.remark}
          render={({ field }) => (
            <CustomTextfield
              {...field}
              width="100%"
              formMb="0"
              spHeight="32px"
              error={Boolean(errors.remark)}
              helperText={errors.remark?.message}
              onBlur={() => changeValue()}
            />
          )}
        />
      </Grid>
    </Grid>
  );
};
